import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { updateUser } from '../../../../../redux/user/actions';

import {
  formatStringOfRolesIntoArray,
  getRoleSimpleList,
} from '../../../../../utils/rolesHelper';
import styles from './styles';

const useStyles = makeStyles(styles);

export const UserRolesComponent = (props) => {
  const {
    tenantUser,
    isLoading,
    onlyOneOwner,
    infoButton,
    onSave,
    onRolesUpdate,
  } = props;
  const dispatch = useDispatch();
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [editRoles, setEditRoles] = useState(formatStringOfRolesIntoArray((tenantUser && tenantUser.roles) || ''));
  const [displayRoles, setDisplayRoles] = useState(formatStringOfRolesIntoArray((tenantUser && tenantUser.roles) || ''));
  const [editPassive, setEditPassive] = useState(tenantUser.passive);
  const permissions = useSelector((state) => state.user.permissions);

  React.useEffect(() => {
    const parsedRoles = formatStringOfRolesIntoArray((tenantUser && tenantUser.roles) || '');
    setEditRoles(parsedRoles);
    setDisplayRoles(parsedRoles);
  }, [tenantUser, tenantUser.roles]);

  const handleOpenPopover = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setEditRoles([...displayRoles]);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = (event) => {
    if (event) {
      event.preventDefault(); event.stopPropagation();
    }
    setAnchorEl(null);
    // revert data to current display values
    setEditRoles([...displayRoles]);
    setEditPassive(tenantUser.passive);
    setRemoveError(null);
  };

  const isCheckboxDisabled = (role) => {
    // user role is always disabled, must be removed from org to remove user permissions
    if (role === 'user') {
      return true;
    }
    // owner role can only be changed by another owner
    // TODO what if all owner roles are removed?
    if (role === 'owner' && !permissions.owner) {
      return true;
    }
    return false;
  };

  const handleSaveRoles = () => {
    if (onSave) {
      const payload = `[${editRoles.join(',')}]`;
      onSave(payload);
      if (onRolesUpdate) {
        onRolesUpdate([...editRoles]);
      }
      handleClosePopover();
      return;
    }

    // must always be one owner
    if (onlyOneOwner && tenantUser.roles.indexOf('owner') >= 0 && editRoles.indexOf('owner') === -1) {
      setRemoveError('There must always be one owner in your organization');
      return;
    }
    setRemoveError(null);

    dispatch(updateUser(tenantUser.id, {
      email: tenantUser.email,
      firstName: tenantUser.firstName,
      lastName: tenantUser.lastName,
      roles: `[${editRoles.join(',')}]`,
      passive: editPassive,
    }, false)).then((updatedUser) => {
      const updatedRoles = formatStringOfRolesIntoArray((updatedUser && updatedUser.roles) || `[${editRoles.join(',')}]`);
      setDisplayRoles(updatedRoles);
      setEditRoles(updatedRoles);
      if (onRolesUpdate) {
        onRolesUpdate(updatedRoles);
      }
      handleClosePopover();
    }).catch(() => {
      handleClosePopover();
    });
  };

  const handleRoleClick = (e) => {
    if (getRoleSimpleList().indexOf(e.target.value) === -1) {
      return;
    }
    let roleList = [...editRoles];
    if (roleList.indexOf(e.target.value) >= 0) {
      roleList = roleList.filter((role) => role !== e.target.value);
    } else {
      roleList.push(e.target.value);
    }
    setEditRoles(roleList);
  };

  const priorityOrder = {
    user: 0,
    farrier: 1,
    veterinarian: 2,
    staff: 3,
    admin: 4,
    owner: 5,
    developer: 6,
  };

  return (
    <div>
      <Tooltip title="Change roles" placement="right">
        <Typography
          variant="body2"
          onClick={(isLoading)
            ? () => { }
            : handleOpenPopover}
          className={classes.rolesTypography}
          component="div"
        >
          {

          }
          {displayRoles.filter((role) => role !== 'restricted')
            .sort((a, b) => priorityOrder[a] - priorityOrder[b])
            .map((r) => (<Chip key={`role-${r}-${tenantUser.email}`} label={r} size="small" sx={{ mr: 0.5 }} />))}
          <Button startIcon={<EditIcon />} aria-label="Edit" className={classes.editButton} size="small" disabled={isLoading}>
            Change Roles
          </Button>
        </Typography>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <List
          dense
          className={classes.listRoot}
          subheader={(
            <ListSubheader component="div" className={removeError && classes.error}>
              {removeError || tenantUser.userFullName}
              <Box
                sx={{
                  margin: 1,
                  display: 'inline',
                }}
              />
              {infoButton}
            </ListSubheader>
          )}
        >
          <Divider />
          <ListItem>
            <ListItemText primary="user" secondary="Only have this checked for contacts or clients" />
            <ListItemSecondaryAction>
              <Checkbox
                edge="end"
                disabled
                checked
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          {getRoleSimpleList().map((role) => (
            <ListItem key={role}>
              <ListItemText primary={role} />
              <ListItemSecondaryAction>
                <Checkbox
                  edge="end"
                  value={role}
                  disabled={editPassive || isCheckboxDisabled(role)}
                  checked={editRoles.indexOf(role) !== -1}
                  onClick={handleRoleClick}
                />
              </ListItemSecondaryAction>
            </ListItem>

          ))}
        </List>
        <Divider />
        <Typography
          variant="caption"
          component="div"
          sx={{
            maxWidth: 350,
            p: 1,
          }}
        >
          {
            (editRoles.indexOf('staff') >= 0 || editRoles.indexOf('admin') >= 0 || editRoles.indexOf('owner') >= 0)
              ? 'This user is getting at least staff level permissions. This gives the user access to administrative pages in Questri.'
              : 'This user is getting basic user or client permissions. They will have basic access to Questri (profile) and any horse access you assign.'
          }
        </Typography>
        <div className={classes.footer}>
          <Button
            onClick={handleClosePopover}
            id="cancel-edit-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRoles}
            color="primary"
            variant="contained"
            autoFocus
            id="confirm-edit-button"
          >
            Save
          </Button>
        </div>
      </Popover>
    </div>
  );
};

UserRolesComponent.propTypes = {
  tenantUser: PropTypes.object.isRequired,
  onlyOneOwner: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  infoButton: PropTypes.any,
  onSave: PropTypes.func, // if passed in, call this instead of saving here
  onRolesUpdate: PropTypes.func,
};
UserRolesComponent.defaultProps = {
  isLoading: false,
  infoButton: {},
  onSave: null,
  onRolesUpdate: null,
};

export default UserRolesComponent;
